import React, { useCallback, useMemo, useEffect, useState } from "react";
import V2ListBoxPageSize from "./v2_listbox_page_size";
import ComboboxAutocomplete from "./combobox_autocomplete";
import { BuildingOffice2Icon, MapPinIcon } from "@heroicons/react/24/outline";
import EmployeesMultiSelectSearchbar from "./employee_multiselect_searchbar";
import { getEmployeesWithContractInRange } from "../services/api_calendar";
import { getWorkingAreaList } from "../services/api_working_area";


export default function SchedulerContextBar({
  units = [],
  selectedUnit,
  onSelectUnit,
  onResetUnit,
  isUnitsLoading,
  token,
  selectedEmployees = [],
  onEmployeesChange,
  pageSize,
  onPageSizeChange,
  contractStartDate,
  contractEndDate,
  selectedWorkingArea = null,
  onWorkingAreaChange,
  showWorkingAreas = false,
  showEmployees = true,
  showPageSize = true,
  showSelectedEmployeesCount = true,
  stickyClassName = "sticky top-4 z-30",
  actions = null,
}) {
  const [workingAreas, setWorkingAreas] = useState([]);
  const [isLoadingAreas, setIsLoadingAreas] = useState(false);
  const [isError, setIsError] = useState(false);

  const handleWorkingAreaChange = (area) => {
    if (!area || area.id === "__ALL__") {
      onWorkingAreaChange?.(null);
      return;
    }
    onWorkingAreaChange?.(area);
  };

  const selectedUnitLabel = selectedUnit
    ? (selectedUnit.name || selectedUnit.label || selectedUnit.descr || "")
    : "Tutte";

  const handleResetUnit = () => {
    onResetUnit?.();
    if (selectedEmployees?.length) onEmployeesChange?.([]);
  };

  const handleResetWorkingArea = () => {
    onWorkingAreaChange?.(null);
  };

  const getEmployeesCb = useCallback((
    tokenArg, q, filterBy = "hr_active", ouId, page, pageSize,
    permission, start, end, sortCfg
  ) => {
    const ouParam = ouId != null ? Number(ouId) : null;

    let workingAreaParam = null;

    if (selectedWorkingArea?.id && selectedWorkingArea.id !== "__NO_AREA__") {
      workingAreaParam = Number(selectedWorkingArea.id);
    }

    return getEmployeesWithContractInRange(
      tokenArg, q, filterBy,
      null,
      page, pageSize,
      ouParam,
      permission,
      start, end,
      sortCfg,
      workingAreaParam
    );
  }, [selectedWorkingArea?.id]); 

  const rolePairs = useMemo(
    () => (selectedEmployees ?? []).map(e => ({ employeeId: e.id })),
    [selectedEmployees]
  );

  useEffect(() => {
    if (!token || !showWorkingAreas) {
      setWorkingAreas([]);
      return;
    }

    setIsLoadingAreas(true);
    setIsError(false);

    getWorkingAreaList(
      token,
      {
        status: "active",
        date: contractStartDate || undefined,
        paginate: false,
      },
      "shifts"
    )
    .then((response) => {
      const areas = Array.isArray(response)
        ? response
        : response?.results || [];

      setWorkingAreas(areas);
    })
    .catch((error) => {
      console.error("Errore caricamento aree di lavoro:", error);

      setWorkingAreas([]);
      setIsError(true);
    })
    .finally(() => {
      setIsLoadingAreas(false);
    });
  }, [token, contractStartDate, contractEndDate, showWorkingAreas]);

  const hasWorkingAreas = showWorkingAreas && workingAreas.length > 0;

  useEffect(() => {
    if (!selectedWorkingArea) {
      return;
    }

    if (selectedWorkingArea.id === "__NO_AREA__") {
      return;
    }

    const areaStillAvailable = workingAreas.some((area) => Number(area.id) === Number(selectedWorkingArea.id));

    if (!areaStillAvailable) {
      onWorkingAreaChange?.(null);
    }
  }, [workingAreas, selectedWorkingArea, onWorkingAreaChange]);

  const ALL_WORKING_AREAS_OPTION = {
    id: "__ALL__",
    name: "Tutte",
  };

  const NO_WORKING_AREA_OPTION = {
    id: "__NO_AREA__",
    name: "Senza area associata",
  };

  return (
    <section className={`${stickyClassName} rounded-xl border border-indigo-100 bg-white/90 backdrop-blur shadow-sm ring-1 ring-black/5 w-full`}>
      <div className="px-4 py-3 md:px-6">
        <div className={`grid grid-cols-1 gap-4 ${hasWorkingAreas ? "lg:grid-cols-2" : "lg:grid-cols-1"}`}>
          {/* UNITÀ ORGANIZZATIVA */}
          <div className={`min-w-0 rounded-lg bg-white ${hasWorkingAreas ? "pr-6 border-r rounded-none border-gray-300" : ''} `}>
            <div className="mb-2 flex items-center gap-2">
                <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                    <BuildingOffice2Icon className="h-6 w-6" />
                </div>

                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-wide text-gray-500">
                    Unità organizzativa
                  </p>

                  <p className={`truncate text-sm font-semibold ${selectedUnit ? "text-indigo-700" : "text-gray-600"}`}>
                    {selectedUnitLabel}
                  </p>
                </div>

                {selectedUnit && (
                  <button
                    type="button"
                    onClick={handleResetUnit}
                    className="ml-auto text-[10px] font-semibold text-indigo-600 hover:underline"
                  >
                    Reset
                  </button>
                )}
            </div>

            {isUnitsLoading ? (
              <span className="inline-block h-9 w-full animate-pulse rounded-md bg-gray-100" />
            ) : (
              <div className="w-full rounded-lg border border-gray-200 shadow-sm">
                <ComboboxAutocomplete
                  options={[{id: "__ALL__", name: "Tutte",}, ...(units || []),]}
                  value={selectedUnit ?? {id: "__ALL__", name: "Tutte",}}
                  alwaysFirstIds={["__ALL__"]}
                  onChange={(next) => {
                    if (!next || next.id === "__ALL__") {
                      handleResetUnit();
                    } else {
                      if (selectedEmployees?.length) {
                        onEmployeesChange?.([]);
                      }
                      onSelectUnit?.(next);
                    }
                  }}
                  placeholder="Seleziona unità..."
                  bgActiveColor="bg-indigo-50"
                  textActiveColor="text-gray-700"
                  positionOptions="absolute"
                  borderClass="border-gray-200 rounded-md"
                />
              </div>
            )}
          </div>

          {/* AREA DI LAVORO */}
          {hasWorkingAreas && (
            <div className="min-w-0 rounded-lg">
              <div className="mb-2 flex items-center gap-2">
                <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                  <MapPinIcon className="h-6 w-6" />
                </div>

                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-wide text-gray-500">
                    Area di lavoro
                  </p>

                  <p className={`truncate text-sm font-semibold ${selectedWorkingArea ? "text-indigo-700" : "text-gray-600"}`}>
                    {selectedWorkingArea?.name || "Tutte"}
                  </p>
                </div>

                {selectedWorkingArea && (
                  <button
                    type="button"
                    onClick={handleResetWorkingArea}
                    className="ml-auto text-[10px] font-semibold text-indigo-600 hover:underline"
                  >
                    Reset
                  </button>
                )}
              </div>

              {isLoadingAreas ? (
                <span className="inline-block h-9 w-full animate-pulse rounded-md bg-gray-100" />
              ) : (
                <div className="w-full rounded-lg border border-gray-200 shadow-sm">
                  <ComboboxAutocomplete
                    options={[
                      ALL_WORKING_AREAS_OPTION,
                      NO_WORKING_AREA_OPTION,
                      ...workingAreas,
                    ]}
                    value={selectedWorkingArea ?? ALL_WORKING_AREAS_OPTION}
                    alwaysFirstIds={["__ALL__", "__NO_AREA__",]}
                    onChange={handleWorkingAreaChange}
                    placeholder="Seleziona area..."
                    bgActiveColor="bg-indigo-50"
                    textActiveColor="text-gray-700"
                    positionOptions="absolute"
                    zIndex="z-[99999]"
                    borderClass="border-gray-200 rounded-md"
                    displayValue={(area) => area?.name || ""}
                    renderOption={(area) => (
                      <div className="flex min-w-0 items-center justify-between gap-3">
                        <span
                          className={`truncate text-sm text-gray-700`}
                          title={area.name}
                        >
                          {area.name}
                        </span>

                        {area.justification_code && (
                          <span className="shrink-0 rounded-md bg-indigo-200 px-2 py-0.5 text-[10px] font-semibold text-indigo-700">
                            {area.justification_code}
                          </span>
                        )}
                      </div>
                    )}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {showEmployees && (
        <>
          <div className="border-t border-gray-100" />

          <div className="px-4 py-3 md:px-6">
            <label className="mb-1 block text-[11px] uppercase tracking-wide text-gray-600">
              Cerca dipendente
            </label>
            <div className="border border-gray-200 rounded-lg">
              <EmployeesMultiSelectSearchbar
                key={`emps-${selectedUnit?.id ?? "all"}-${selectedWorkingArea?.id ?? "all"}-${contractStartDate}-${contractEndDate}`}
                token={token}
                selectedEmployees={selectedEmployees}
                onChange={onEmployeesChange}
                getEmployees={getEmployeesCb}
                placeholder="Digita per cercare…"
                employeeRolePairs={rolePairs}
                ou={selectedUnit?.id ?? null}
                pageSize={10}
                contractStartDate={contractStartDate}
                contractEndDate={contractEndDate}
              />
            </div>
          </div>
        </>
      )}
      
      <div className="rounded-b-2xl bg-gray-50 px-4 py-2 md:px-6">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {showSelectedEmployeesCount && (
              <div className="text-xs text-gray-500">
                {selectedEmployees?.length || 0} dipendent
                {selectedEmployees?.length === 1 ? "e" : "i"} selezionat
                {selectedEmployees?.length === 1 ? "o" : "i"}
              </div>
            )}

            {actions && (
              <div className="flex items-center">
                {actions}
              </div>
            )}
          </div>

          {showPageSize && (
            <div className="w-full sm:w-48" style={{ position: "relative", zIndex: 25 }}>
              <V2ListBoxPageSize value={pageSize} onChange={onPageSizeChange} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}